import { z } from 'zod';

import {
	Box,
	Form,
	Stack,
} from '@ekhodealer/ekho-common/rev';

import type { AiBdcSettings } from '../types';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';

import { useGetAiBdcSettings } from '../api/useGetAiBdcSettings';
import { useUpdateAiBdcSettings } from '../api/useUpdateAiBdcSettings';

import { ConversationBehaviorSection } from './ConversationBehaviorSection';

const LANGUAGE_OPTIONS = [
	{ id: 'English', label: 'English' },
	{ id: 'Spanish', label: 'Spanish' },
	{ id: 'French', label: 'French' },
	{ id: 'German', label: 'German' },
	{ id: 'Portuguese', label: 'Portuguese' },
	{ id: 'Italian', label: 'Italian' },
	{ id: 'Dutch', label: 'Dutch' },
	{ id: 'Russian', label: 'Russian' },
	{ id: 'Chinese (Simplified)', label: 'Chinese (Simplified)' },
	{ id: 'Chinese (Traditional)', label: 'Chinese (Traditional)' },
	{ id: 'Japanese', label: 'Japanese' },
	{ id: 'Korean', label: 'Korean' },
	{ id: 'Arabic', label: 'Arabic' },
	{ id: 'Hindi', label: 'Hindi' },
	{ id: 'Turkish', label: 'Turkish' },
	{ id: 'Polish', label: 'Polish' },
	{ id: 'Swedish', label: 'Swedish' },
	{ id: 'Vietnamese', label: 'Vietnamese' },
	{ id: 'Thai', label: 'Thai' },
	{ id: 'Indonesian', label: 'Indonesian' },
	{ id: 'Czech', label: 'Czech' },
	{ id: 'Romanian', label: 'Romanian' },
	{ id: 'Ukrainian', label: 'Ukrainian' },
	{ id: 'Greek', label: 'Greek' },
	{ id: 'Hebrew', label: 'Hebrew' },
];

const displayNameSchema = z.object({
	displayName: z.string().max(50).optional().default(''),
});

const defaultLanguageSchema = z.object({
	defaultLanguage: z.string().min(1, 'A default language is required'),
});

const languagesSchema = z.object({
	languages: z.array(z.string()).max(5, 'You can feature up to 5 languages').default([]),
});

type DisplayNameFormData = z.infer<typeof displayNameSchema>;
type DefaultLanguageFormData = z.infer<typeof defaultLanguageSchema>;
type LanguagesFormData = z.infer<typeof languagesSchema>;

function getDisplayNameDefaults(settings: AiBdcSettings | undefined): DisplayNameFormData {
	return {
		displayName: settings?.displayName || 'Duke',
	};
}

function getDefaultLanguageDefaults(settings: AiBdcSettings | undefined): DefaultLanguageFormData {
	return {
		defaultLanguage: settings?.defaultLanguage ?? 'English',
	};
}

function getLanguagesDefaults(settings: AiBdcSettings | undefined): LanguagesFormData {
	return {
		languages: settings?.featuredLanguages?.length ? settings.featuredLanguages : ['English'],
	};
}

export function IdentitySection() {
	const spId = useCurrentServicesProviderId();
	const { data: settings } = useGetAiBdcSettings(spId);
	const updateMutation = useUpdateAiBdcSettings(spId);

	const handleDisplayNameSubmit = async (data: DisplayNameFormData): Promise<{ isSuccess: boolean }> => {
		try {
			await updateMutation.mutateAsync({ displayName: data.displayName });
			return { isSuccess: true };
		} catch {
			return { isSuccess: false };
		}
	};

	const handleDefaultLanguageSubmit = async (data: DefaultLanguageFormData): Promise<{ isSuccess: boolean }> => {
		try {
			await updateMutation.mutateAsync({ defaultLanguage: data.defaultLanguage });
			return { isSuccess: true };
		} catch {
			return { isSuccess: false };
		}
	};

	const handleLanguagesSubmit = async (data: LanguagesFormData): Promise<{ isSuccess: boolean }> => {
		try {
			await updateMutation.mutateAsync({
				featuredLanguages: data.languages.length > 0 ? data.languages : undefined,
			});
			return { isSuccess: true };
		} catch {
			return { isSuccess: false };
		}
	};

	return (
		<Stack itemsSpacing='16'>
			<Box padding='24' background='secondary' rounding='12'>
				<Form
					name='ai-bdc-display-name'
					schema={displayNameSchema}
					defaultValues={getDisplayNameDefaults(settings)}
					onSubmit={handleDisplayNameSubmit}
					title='Display name'
					titleLevel='subgroup'
					titleWeight='semibold'
					description='The name your AI assistant uses when communicating with leads.'
					showSaveAndClear>
					<Form.Field.Text
						schemaField='displayName'
						labelText='Display name'
						size='large'
						placeholder='E.g., Julian'
					/>
				</Form>
			</Box>
			<Box padding='24' background='secondary' rounding='12'>
				<Form
					name='ai-bdc-default-language'
					schema={defaultLanguageSchema}
					defaultValues={getDefaultLanguageDefaults(settings)}
					onSubmit={handleDefaultLanguageSubmit}
					title='Default language'
					titleLevel='subgroup'
					titleWeight='semibold'
					description='The language your AI assistant responds in by default. If a visitor writes in a different language, the assistant will automatically switch to match.'
					showSaveAndClear>
					<Form.Field.Dropdown
						schemaField='defaultLanguage'
						labelText='Default language'
						options={LANGUAGE_OPTIONS}
					/>
				</Form>
			</Box>
			<Box padding='24' background='secondary' rounding='12' showOverflow>
				<Form
					name='ai-bdc-languages'
					schema={languagesSchema}
					defaultValues={getLanguagesDefaults(settings)}
					onSubmit={handleLanguagesSubmit}
					title='Featured languages'
					titleLevel='subgroup'
					titleWeight='semibold'
					description='Your AI assistant automatically responds in whatever language a visitor uses. Featured languages are highlighted in the chat widget so visitors know they can communicate in those languages. List the languages your clientele typically speak — up to five.'
					showSaveAndClear>
					<Form.Field.ChipPicker
						schemaField='languages'
						options={LANGUAGE_OPTIONS}
						labelText='Featured languages'
						placeholder='Search for a language'
						emptyText='No languages selected'
						showResultsOnFocus
						maxItems={5}
					/>
				</Form>
			</Box>
			<ConversationBehaviorSection />
		</Stack>
	);
}
